import { Fragment, useCallback, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import ContextMenuComponent from '../../../components/stories/organisms/context-menu'
import {
  contextMenuAlignOptions,
  contextMenuArrowAlignmentOptions,
  contextMenuPaddingOptions,
  contextMenuSideOptions,
  contextMenuTitleSizeOptions,
  contextMenuTitleWeightOptions,
  contextMenuVariantOptions
} from '../../../components/stories/organisms/context-menu/constants'
import TableEditButton from '../../../components/stories/organisms/table/components/table-edit-button'
import { useSnackbar } from '../../../components/stories/molecules/snackbar/component/snackbar-hook'
import { snackbarVariantOptions } from '../../../components/stories/molecules/snackbar/constants'
import { ContextMenuTitle } from '../styled-component'
import MoveMultipleItemsModal from '../components/move-items-modals/move-multiple-items-modal'
import { getIsAdmin } from '../../../react-query/authentication/query/use-me-query'
import { useIntl } from 'react-intl'
import { planTreeItemTypeOptions } from '../../../react-query/plan/query/use-get-plan-tree-query'
import DeactivatePlanAlert from './deactivate-plan-alert'
import { useUpdateBulkPlansMutation } from '../../../react-query/plan/mutation/use-update-bulk-plans-mutation'

const PlanMenuBulkContextMenu = ({
  onBatchComplete = () => {},
  selectedItems = []
}) => {
  const { formatMessage } = useIntl()
  const { addSnackbar } = useSnackbar()
  const { projectId } = useParams()
  const [isOpen, setIsOpen] = useState(false)
  const [isMoveModalOpen, setIsMoveModalOpen] = useState(false)
  const [isSetInactivePopupOpen, setIsSetInactivePopupOpen] = useState(false)

  const selectedPlans = useMemo(
    () =>
      selectedItems.filter(
        (item) => item.type === planTreeItemTypeOptions.PLAN
      ),
    [selectedItems]
  )

  const selectedFolders = useMemo(
    () =>
      selectedItems.filter(
        (item) => item.type === planTreeItemTypeOptions.FOLDER
      ),
    [selectedItems]
  )

  const selectedPlanActivatedIds = useMemo(
    () => selectedPlans.filter((plan) => plan.active).map((plan) => plan.id),
    [selectedPlans]
  )

  const selectedPlanInactivatedIds = useMemo(
    () => selectedPlans.filter((plan) => !plan.active).map((plan) => plan.id),
    [selectedPlans]
  )

  const hasInactivePlans = selectedPlanInactivatedIds.length > 0
  const hasActivePlans = selectedPlanActivatedIds.length > 0
  const hasSelectedItems = selectedItems.length > 0
  const hasSelectedPlans = selectedPlans.length > 0

  const isAdmin = getIsAdmin()

  const handleUpdatePlansError = useCallback(() => {
    addSnackbar({
      message: formatMessage({ id: 'error' }),
      snackbarType: snackbarVariantOptions.ERROR,
      hideCloseButton: true
    })
  }, [addSnackbar, formatMessage])

  const showSnackbarPlanToggleIsActiveSuccess = (data) => {
    const { active } = data[0]

    const updatedPlansCount = data.length ?? 1

    addSnackbar({
      message: formatMessage(
        {
          id: active
            ? 'plans_message_reactivate-success'
            : 'plans_message_deactivate-success'
        },
        { count: updatedPlansCount }
      ),
      snackbarType: snackbarVariantOptions.SUCCESS,
      hideCloseButton: true
    })
  }

  // mutates
  const handlePlansUpdated = useCallback(
    (data) => {
      showSnackbarPlanToggleIsActiveSuccess(data)
      onBatchComplete()
    },
    [addSnackbar, formatMessage, onBatchComplete]
  )

  const { mutate: updatePlansBatch, isPending: isUpdatingPlansBatch } =
    useUpdateBulkPlansMutation({
      onSuccess: handlePlansUpdated,
      onError: handleUpdatePlansError
    })

  // handlers
  const handleTriggerClick = useCallback((event) => {
    event.stopPropagation()
  }, [])

  const handleMoveItems = useCallback(() => {
    setIsMoveModalOpen(true)
  }, [])

  const handleSetPlansInactive = useCallback(() => {
    if (!hasSelectedPlans) return

    setIsSetInactivePopupOpen(true)
  }, [hasSelectedPlans])

  const handleConfirmSetPlansInactive = useCallback(() => {
    const plansToDeactivate = selectedPlanActivatedIds.map((planId) => ({
      id: planId,
      active: false
    }))

    updatePlansBatch({ plans: plansToDeactivate, projectId })
  }, [selectedPlanActivatedIds, updatePlansBatch, projectId])

  const handleConfirmSetPlansActive = useCallback(() => {
    const plansToActivate = selectedPlanInactivatedIds.map((planId) => ({
      id: planId,
      active: true
    }))

    updatePlansBatch({ plans: plansToActivate, projectId })
  }, [selectedPlanInactivatedIds, updatePlansBatch, projectId])

  const handleItemClick = (item) => (event) => {
    event.stopPropagation()

    if (item.disabled) return

    setIsOpen(false)
    item.action()
  }

  const isInactiveOnlySelection = hasInactivePlans && !hasActivePlans

  // A selection holding both states needs the label to say *which* plans the
  // action touches ("Deactivate active plans"); when everything selected is
  // already active there is nothing to disambiguate, so the plain verb is used.
  const isMixedStateSelection = hasActivePlans && hasInactivePlans

  const menuItems = useMemo(
    () =>
      [
        {
          id: 'move-selected-items',
          title: formatMessage({ id: 'move' }),
          action: handleMoveItems,
          hide: !isAdmin || isInactiveOnlySelection,
          showDivider: true
        },
        {
          id: 'plans_action_reactivate',
          title: formatMessage(
            { id: 'plans_action_reactivate' },
            { count: selectedPlanInactivatedIds.length }
          ),
          action: handleConfirmSetPlansActive,
          hide: !isAdmin || !isInactiveOnlySelection,
          variant: contextMenuVariantOptions.STANDARD,
          showDivider: false
        },
        {
          id: 'plans_action_deactivate',
          title: isMixedStateSelection
            ? formatMessage(
                { id: 'plans_action_deactivate' },
                { count: selectedPlanActivatedIds.length }
              )
            : formatMessage({ id: 'deactivate' }),
          action: handleSetPlansInactive,
          hide: !isAdmin || !hasActivePlans,
          variant: contextMenuVariantOptions.DESTRUCTIVE,
          showDivider: false
        }
      ].filter(({ hide }) => !hide),
    [
      formatMessage,
      handleConfirmSetPlansActive,
      handleMoveItems,
      handleSetPlansInactive,
      hasActivePlans,
      isAdmin,
      isInactiveOnlySelection,
      isMixedStateSelection,
      selectedPlanActivatedIds,
      selectedPlanInactivatedIds
    ]
  )

  const isDisabled =
    !hasSelectedItems || !menuItems.length || isUpdatingPlansBatch

  if (isDisabled) {
    return <TableEditButton disabled />
  }

  return (
    <>
      <ContextMenuComponent.Root isOpen={isOpen} setIsOpen={setIsOpen}>
        <ContextMenuComponent.Trigger callback={handleTriggerClick}>
          <TableEditButton disabled={false} />
        </ContextMenuComponent.Trigger>
        <ContextMenuComponent.List
          width="220px"
          align={contextMenuAlignOptions.START}
          side={contextMenuSideOptions.BOTTOM}
          arrow
          arrowAlignment={contextMenuArrowAlignmentOptions.END}
        >
          <ContextMenuTitle>
            {formatMessage(
              { id: 'x_item_selected' },
              { item: selectedItems.length }
            )}
          </ContextMenuTitle>
          {menuItems.map((item) => (
            <Fragment key={item.id}>
              <ContextMenuComponent.Item
                id={item.id}
                variant={item.variant}
                padding={contextMenuPaddingOptions['EXTRA-LARGE']}
                titleWeight={contextMenuTitleWeightOptions['TEXT-600']}
                titleSize={contextMenuTitleSizeOptions['TEXT-R']}
                onClick={handleItemClick(item)}
                title={item.title}
                disabled={item.disabled}
              />
              {item.showDivider && <ContextMenuComponent.Divider />}
            </Fragment>
          ))}
        </ContextMenuComponent.List>
      </ContextMenuComponent.Root>

      {/* export plans in bulk here */}

      {isMoveModalOpen && (
        <MoveMultipleItemsModal
          onClose={() => setIsMoveModalOpen(false)}
          onMoved={onBatchComplete}
          selectedFolders={selectedFolders}
          selectedPlans={selectedPlans}
        />
      )}

      {isSetInactivePopupOpen && (
        <DeactivatePlanAlert
          onClose={() => setIsSetInactivePopupOpen(false)}
          onConfirm={handleConfirmSetPlansInactive}
          isLoading={isUpdatingPlansBatch}
          count={selectedPlanActivatedIds.length}
        />
      )}
    </>
  )
}

export default PlanMenuBulkContextMenu
